import { useMemo, useState } from "react";
import { routes } from "@/router";
import { buildCurrentDynamicFormDefinition } from "@/utils/buildCurrentDynamicFormDefinition";
import { buildDefaultValues, formatAnswers } from "@/utils/dynamicForm.util";
import { PredictiveCampaignAnswerDto } from "@advanced-sales-technologies/backend-client/models/PredictiveCampaignAnswerDto";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { FormValues } from "@/types/DynamicForm.type";
import { useCreateCampaign } from "@/hooks/api/predictive/campaign/useCreateCampaign";
import { useGetCampaignQuestions } from "@/hooks/api/predictive/campaign/useGetCampaignQuestions";
import { useSubmitCampaign } from "@/hooks/api/predictive/campaign/useSubmitCampaign";
import Spinner from "@/components/atoms/Spinner";
import DynamicForm from "@/components/molecules/form/DynamicForm";
import { SETUP_STEP, steps } from "@/components/molecules/predictive/campaignFormSteps";
import { campaignFormDefinition, CampaignFormValues } from "@/components/organisms/predictive/campaignFormDefinition";
import PredictiveLeadsHeader from "@/components/organisms/predictive/PredictiveLeadsHeader";

const CreateCampaign = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const { productId } = useParams();
  const navigate = useNavigate();

  const { data: questions, isLoading: questionsLoading } = useGetCampaignQuestions();
  const { mutateAsync: createCampaignAsync, isPending: createCampaignPending } = useCreateCampaign();
  const { mutateAsync: submitAndSubscribeToCampaignAsync, isPending: submitCampaignPending } = useSubmitCampaign();

  const currentCampaignDefinition = useMemo(
    () =>
      buildCurrentDynamicFormDefinition(campaignFormDefinition(t), questions, {
        includeName: true,
        includeHeaders: true,
        alwaysInclude: ["subscription"],
      }),
    [campaignFormDefinition, questions],
  );

  const defaultValues = useMemo(() => buildDefaultValues(currentCampaignDefinition), [currentCampaignDefinition]);

  const handleCreate = async (data: CampaignFormValues, isSubmit = false, step: SETUP_STEP) => {
    // Pull out name, subscription separately
    const { name, subscription, ...answers } = data;
    const formatted = formatAnswers(
      currentCampaignDefinition,
      answers as FormValues<typeof currentCampaignDefinition>,
    ) as PredictiveCampaignAnswerDto[];

    const planId = (subscription as { id: string }).id;

    const campaign = await createCampaignAsync({
      name: name as string,
      icon: null,
      subscriptionId: planId,
      productId: productId!,
      answers: formatted,
      formStep: step,
      displayToast: true,
    });

    if (campaign && isSubmit) {
      const response = await submitAndSubscribeToCampaignAsync(campaign.id);
      if (response.url) {
        // Handle payment via Stripe
        window.location.replace(response.url);
      }
    } else {
      navigate(routes.predictive);
    }
  };

  const handleDraft = (data: CampaignFormValues) => {
    const step = steps[currentStep - 1] ?? SETUP_STEP.INSIGHTS;
    handleCreate(data, false, step);
  };

  const handleSubmit = (data: CampaignFormValues) => {
    handleCreate(data, true, SETUP_STEP.INSIGHTS);
  };

  return (
    <div className={"h-screen w-full overflow-y-auto px-12"}>
      <PredictiveLeadsHeader steps={3} showIntroButton activeStep={currentStep} />

      {questionsLoading ? (
        <div className={"flex h-full items-center justify-center"}>
          <Spinner />
        </div>
      ) : (
        <div className={"mx-auto mt-12 max-w-xl"}>
          <DynamicForm
            config={currentCampaignDefinition}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onSaveDraft={handleDraft}
            onStepChanged={setCurrentStep}
            isPending={submitCampaignPending || createCampaignPending}
            submitBtnText={t("predictive.campaign.submit")}
            draftBtnText={t("predictive.campaign.saveDraft")}
          />
        </div>
      )}
    </div>
  );
};

export default CreateCampaign;
